import React, { useEffect, useRef } from 'react'
import { Tooltip } from '@chakra-ui/react'
import { usePostEducationV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import './ApplyV2EducationStep2Add.scss'

interface Props {
    className?: string
    onClose?: () => void
}

interface FormValues {
    schoolName: string //Educational Institution
    degree: string | null | undefined
    educationType: string | null | undefined // Field of study
    location: string | undefined | null
    description: string | null | undefined
    startMonth: string | null | undefined
    startYear: string | null | undefined
    endMonth: string | null | undefined
    endYear: string | null | undefined
    // educationListId?: number
    // existingId
}

const baseClass = 'ApplyV2EducationStep2Add'

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 90 }, (_, i) => (currentYear - i).toString())
const yearOptions = years.map((y) => ({ value: y, label: y }))
const endYears = Array.from({ length: 90 }, (_, i) => (currentYear + 10 - i).toString())
const endYearsOptions = endYears.map((y) => ({ value: y, label: y }))
const parseDateString = (dateString: string | null | undefined) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'null') {
        return { year: '', month: '' }
    }

    try {
        const [year, month, day] = dateString.split('-')
        const yearNum = parseInt(year, 10)
        const monthNum = parseInt(month, 10)

        if (isNaN(yearNum) || yearNum < 1900 || yearNum > 2100) {
            return { year: '', month: '' }
        }

        if (isNaN(monthNum) || monthNum < 0 || monthNum > 12) {
            return {
                year: yearNum.toString(),
                month: '',
            }
        }

        return {
            year: yearNum.toString(),
            month: monthNum < 10 ? `0${monthNum}` : `${monthNum}`,
        }
    } catch {
        return { year: '', month: '' }
    }
}

function ApplyV2EducationStep2Add({ className, onClose = () => {} }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const { storageData, postProfileRefetch } = useApplyV2Context()
    const [postEducation, { isLoading: isPostLoading }] = usePostEducationV2ApplyMutation()
    const firstRef = useRef<any>(null)

    useEffect(() => {
        if (firstRef.current) {
            firstRef.current.focus()
        }
    }, [])

    const months = [
        { value: '00', label: ' -- ' },
        { value: '01', label: getTitle('477', 'January') },
        { value: '02', label: getTitle('478', 'February') },
        { value: '03', label: getTitle('479', 'March') },
        { value: '04', label: getTitle('480', 'April') },
        { value: '05', label: getTitle('481', 'May') },
        { value: '06', label: getTitle('482', 'June') },
        { value: '07', label: getTitle('483', 'July') },
        { value: '08', label: getTitle('484', 'August') },
        { value: '09', label: getTitle('485', 'September') },
        { value: '10', label: getTitle('486', 'October') },
        { value: '11', label: getTitle('487', 'November') },
        { value: '12', label: getTitle('488', 'December') },
    ]

    const monthOptions = months.map((m) => ({ value: m.value, label: m.label }))

    const validationSchema = Yup.object().shape({
        schoolName: Yup.string()
            .trim()
            .required(getTitle('5653', 'Required'))
            .min(2, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`)
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        degree: Yup.string()
            .nullable()
            .trim()
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        educationType: Yup.string()
            .nullable()
            .trim()
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        location: Yup.string()
            .nullable()
            .trim()
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        description: Yup.string()
            .trim()
            .nullable()
            .max(5000, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 5000 })}`),
        startMonth: Yup.string().required(getTitle('5653', 'Required')),
        startYear: Yup.string().required(getTitle('5653', 'Required')),
        endMonth: Yup.string()
            .required(getTitle('5653', 'Required'))
            .test('is-after-start', getTitle('9453', 'End date cannot be earlier than start date'), function (value) {
                const { startMonth, startYear, current } = this.parent

                if (current) return true
                if (!value || !startMonth || !startYear) return true

                const startDate = dayjs(`${startYear}-${startMonth}-01`)
                const endDate = dayjs(`${this.parent.endYear}-${value}-01`)

                return endDate.isAfter(startDate) || endDate.isSame(startDate, 'month')
            }),
        endYear: Yup.string()
            .required(getTitle('5653', 'Required'))
            .test('is-after-start', getTitle('9453', 'End date cannot be earlier than start date'), function (value) {
                const { startMonth, startYear, endMonth, current } = this.parent

                if (current) return true
                if (!value || !startMonth || !startYear || !endMonth) return true

                const startDate = dayjs(`${startYear}-${startMonth}-01`)
                const endDate = dayjs(`${value}-${endMonth}-01`)

                return endDate.isAfter(startDate) || endDate.isSame(startDate, 'month')
            }),
    })

    const formik = useFormik<FormValues>({
        initialValues: {
            schoolName: '',
            degree: '',
            educationType: '',
            location: '',
            description: '',
            startMonth: '',
            startYear: '',
            endMonth: '',
            endYear: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                const uid = storageData?.postInitUid

                const res = await postEducation({
                    uid: uid ?? '',
                    body: {
                        schoolName: values.schoolName,
                        degree: values.degree || null,
                        educationType: values.educationType || null,
                        location: values.location || undefined,
                        description: values.description || null,
                        fromDate: `${values.startYear}-${values.startMonth}-01`,
                        toDate: `${values.endYear}-${values.endMonth}-01`,
                        educationListId: 0,
                    },
                }).unwrap()
                postProfileRefetch()
            } catch (error: any) {
                if (error?.data?.errors && Array.isArray(error.data.errors)) {
                    const formikErrors: { [key: string]: string } = {}

                    error.data.errors.forEach((err: any) => {
                        switch (err.field) {
                            case 'fromDate':
                                formikErrors.startYear = err.msg
                                formikErrors.startMonth = err.msg
                                break
                            case 'toDate':
                                formikErrors.endYear = err.msg
                                formikErrors.endMonth = err.msg
                                break
                            case 'schoolName':
                            case 'degree':
                            case 'location':
                            case 'educationType':
                            case 'description':
                                formikErrors[err.field] = err.msg
                                break
                            default:
                                formikErrors.root = err.msg
                                break
                        }
                    })

                    setErrors(formikErrors)
                } else {
                    setErrors({
                        root: error.message,
                    } as any)
                }
            }
        },
        enableReinitialize: true,
        validateOnChange: true,
    })

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('1255', 'Add education')}</div>
                <Tooltip
                    hasArrow
                    // isDisabled={isLoading}
                    openDelay={500}
                    label={getTitle('181', 'Close')}
                    // placement={'right'}
                    padding={'8px 16px'}
                    borderRadius={'4px'}
                    backgroundColor={'#19191A'}
                    fontWeight={600}
                    fontSize={'12px'}
                    shouldWrapChildren
                >
                    <ApplyV2Button
                        className={`${baseClass}__header__btn`}
                        size={'small'}
                        variant="transparent"
                        onClick={onClose}
                        leftIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    />
                </Tooltip>
            </div>
            <ApplyV2InputText
                ref={firstRef}
                name="schoolName"
                label={getTitle('6710', 'Educational Institution')}
                value={formik.values.schoolName}
                onChange={(e) => {
                    // formik.setFieldTouched('schoolName', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('6711', 'Your school, university, etc')}
                errorMessage={formik.touched.schoolName && formik.errors.schoolName}
                isRequired
            />
            <ApplyV2InputText
                name="degree"
                label={getTitle('6724', 'Degree')}
                value={formik.values.degree}
                onChange={(e) => {
                    // formik.setFieldTouched('degree', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('6725', 'Bachelor, Master, etc')}
                errorMessage={formik.touched.degree && formik.errors.degree}
                // isRequired
            />
            <ApplyV2InputText
                name="educationType"
                label={getTitle('3293', 'Field of study')}
                value={formik.values.educationType}
                onChange={(e) => {
                    // formik.setFieldTouched('educationType', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('8686', 'E.g. Computer Science')}
                errorMessage={formik.touched.educationType && formik.errors.educationType}
                // isRequired
            />
            <ApplyV2InputText
                name="location"
                label={getTitle('1249', 'Location')}
                value={formik.values.location}
                onChange={(e) => {
                    // formik.setFieldTouched('location', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('1249', 'City, Country')}
                errorMessage={formik.touched.location && formik.errors.location}
                // isRequired
            />

            <div className={`${baseClass}__dates`}>
                <div className={`${baseClass}__dates--label`}>{getTitle('325', 'Start date')}*</div>
                <div className={`${baseClass}__dates--row`}>
                    <ApplyV2Select
                        className={`${baseClass}__dates--row--select ${baseClass}__dates--row--select-month`}
                        name="startMonth"
                        placeholder={getTitle('931', 'Month')}
                        value={formik.values.startMonth}
                        options={monthOptions}
                        onChange={(e) => {
                            formik.setFieldValue('startMonth', e)
                        }}
                        onBlur={formik.handleBlur}
                        isRequired
                        isSearchable
                        errorMessage={formik.touched.startMonth && formik.errors.startMonth}
                        isShowErrorMessage={false}
                        onInputChange={(input) => {
                            return input.slice(0, 20)
                        }}
                    />
                    <ApplyV2Select
                        className={`${baseClass}__dates--row--select`}
                        name="startYear"
                        placeholder="YYYY"
                        value={formik.values.startYear}
                        options={yearOptions}
                        onChange={(e) => {
                            formik.setFieldValue('startYear', e)
                        }}
                        onBlur={formik.handleBlur}
                        isRequired
                        isSearchable
                        errorMessage={formik.touched.startYear && formik.errors.startYear}
                        isShowErrorMessage={false}
                        onInputChange={(input) => {
                            return input.replace(/\D/g, '').slice(0, 4)
                        }}
                    />
                </div>
                {((formik.touched.startYear && formik.errors.startYear) ||
                    (formik.touched.startMonth && formik.errors.startMonth)) && (
                    <div className={`${baseClass}__dates__error`}>
                        <div className={`${baseClass}__dates__error--icon`}>
                            <WarningErrorIcon />
                        </div>
                        <div className={`${baseClass}__dates__error--text`}>
                            {[...new Set([formik.errors.startMonth, formik.errors.startYear])]
                                .filter(Boolean)
                                .join(', ')}
                        </div>
                    </div>
                )}
            </div>
            <div className={`${baseClass}__dates`}>
                <div className={`${baseClass}__dates--label`}>{getTitle('326', 'End date')}*</div>
                <div className={`${baseClass}__dates--row`}>
                    <ApplyV2Select
                        className={`${baseClass}__dates--row--select ${baseClass}__dates--row--select-month`}
                        name="endMonth"
                        placeholder={getTitle('931', 'Month')}
                        value={formik.values.endMonth}
                        options={monthOptions}
                        onChange={(e) => {
                            formik.setFieldValue('endMonth', e)
                        }}
                        onBlur={formik.handleBlur}
                        isRequired
                        isSearchable
                        errorMessage={formik.touched.endMonth && formik.errors.endMonth}
                        isShowErrorMessage={false}
                        onInputChange={(input) => {
                            return input.slice(0, 20)
                        }}
                    />
                    <ApplyV2Select
                        className={`${baseClass}__dates--row--select`}
                        name="endYear"
                        placeholder="YYYY"
                        value={formik.values.endYear}
                        // options={yearOptions}
                        options={endYearsOptions.filter((it) => {
                            const currYear = dayjs(it.value)
                            const startYear = dayjs(formik.values.startYear)
                            if (!startYear.isValid()) {
                                return true
                            }
                            return !currYear.isBefore(startYear)
                        })}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                            formik.setFieldValue('endYear', e)
                        }}
                        onBlur={formik.handleBlur}
                        isRequired
                        isSearchable
                        errorMessage={formik.touched.endYear && formik.errors.endYear}
                        isShowErrorMessage={false}
                        onInputChange={(input) => {
                            return input.replace(/\D/g, '').slice(0, 4)
                        }}
                    />
                </div>
                {((formik.touched.endYear && formik.errors.endYear) ||
                    (formik.touched.endMonth && formik.errors.endMonth)) && (
                    <div className={`${baseClass}__dates__error`}>
                        <div className={`${baseClass}__dates__error--icon`}>
                            <WarningErrorIcon />
                        </div>
                        <div className={`${baseClass}__dates__error--text`}>
                            {[...new Set([formik.errors.endMonth, formik.errors.endYear])].filter(Boolean).join(', ')}
                        </div>
                    </div>
                )}
            </div>

            <ApplyV2InputTextarea
                name="description"
                label={getTitle('606', 'Description')}
                value={formik.values.description}
                onChange={(e) => {
                    // formik.setFieldTouched('description', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('1650', 'Enter description')}
                errorMessage={formik.touched.description && formik.errors.description}
            />
            <div className={`${baseClass}__btns`}>
                <ApplyV2Button
                    type={'reset'}
                    variant="transparent"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    // isLoading={formik.isSubmitting}
                    onClick={() => {
                        // console.log(formik.values, formik.touched, formik.errors)
                        formik.resetForm()
                        onClose()
                    }}
                >
                    {getTitle('416', 'Cancel')}
                </ApplyV2Button>
                <ApplyV2Button
                    type={'submit'}
                    // type={'button'}
                    variant="primary"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    isLoading={formik.isSubmitting}
                    isDisabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    // isDisabled={!formik.isValid || !formik.dirty}
                    // onClick={() => {
                    //     console.log('Submit clicked', formik.values)
                    //     console.log('Formik state:', formik.touched)
                    //     console.log('Formik errors:', formik.errors)
                    //     console.log('Formik isValid:', formik.isValid, formik.dirty)
                    // }}
                >
                    {getTitle('179', 'Add')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

export default ApplyV2EducationStep2Add
